import type {
  TabularColumnDescriptionEdit,
  TabularConfigReviseRequest
} from '@atbash-labs/ontology-ingest'
import type { ListIngestMappingConfigsFilters } from '@atbash-labs/proxy-client'

import { getOntologyServiceBaseUrl } from '../../config'
import { ingestApi } from './ingest-api'
import type { IngestMappingConfigSummary } from './types'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export type ListIngestMappingConfigsInput = Omit<ListIngestMappingConfigsFilters, 'kind'> & {
  kind: 'mapped_tabular_ingest'
}

/** The revise call's body, as the api-service defines it. */
export type ReviseIngestMappingConfigInput = TabularConfigReviseRequest
export type ColumnDescriptionEdit = TabularColumnDescriptionEdit

function listPath(input: ListIngestMappingConfigsInput): string {
  const params = new URLSearchParams({ kind: input.kind })
  for (const [key, value] of Object.entries(input)) {
    if (key !== 'kind' && value !== undefined) params.set(key, String(value))
  }
  return `${ONTOLOGY_SERVICE_URL}/ingest/mapping-configs?${params.toString()}`
}

const mappingConfigsApi = ingestApi.injectEndpoints({
  endpoints: (builder) => ({
    listIngestMappingConfigs: builder.query<
      IngestMappingConfigSummary[],
      ListIngestMappingConfigsInput
    >({
      query: listPath,
      providesTags: (result) => [
        { type: 'IngestMappingConfigs', id: 'LIST' },
        ...(result ?? []).map((config) => ({
          type: 'IngestMappingConfigs' as const,
          id: config.id
        }))
      ]
    }),
    getIngestMappingConfig: builder.query<IngestMappingConfigSummary, string>({
      query: (id) => `${ONTOLOGY_SERVICE_URL}/ingest/mapping-configs/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'IngestMappingConfigs', id }]
    }),
    reviseIngestMappingConfig: builder.mutation<
      IngestMappingConfigSummary,
      ReviseIngestMappingConfigInput
    >({
      query: ({ id, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/configs/${encodeURIComponent(id)}/revise`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'IngestMappingConfigs', id: 'LIST' }]
    }),
    /** Archives one version row; the page calls it once per active version of a name. */
    archiveIngestMappingConfig: builder.mutation<IngestMappingConfigSummary, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapping-configs/${encodeURIComponent(id)}/archive`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'IngestMappingConfigs', id: 'LIST' },
        { type: 'IngestMappingConfigs', id }
      ]
    }),
    /** Clears one version row's archived mark; the page calls it once per restored version. */
    unarchiveIngestMappingConfig: builder.mutation<IngestMappingConfigSummary, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapping-configs/${encodeURIComponent(id)}/unarchive`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'IngestMappingConfigs', id: 'LIST' },
        { type: 'IngestMappingConfigs', id }
      ]
    })
  })
})

export const {
  useListIngestMappingConfigsQuery,
  useGetIngestMappingConfigQuery,
  useReviseIngestMappingConfigMutation,
  useArchiveIngestMappingConfigMutation,
  useUnarchiveIngestMappingConfigMutation
} = mappingConfigsApi
